import { useGameStore } from '../store/gameStore';

/**
 * TrackModal — full-screen overlay showing track details.
 * Opens when clicking a building. Closes on X or click-outside.
 */
export default function TrackModal() {
  const modalTrackId = useGameStore((s) => s.modalTrackId);
  const closeModal = useGameStore((s) => s.closeModal);
  const lockTrack = useGameStore((s) => s.lockTrack);
  const lockedTrackId = useGameStore((s) => s.lockedTrackId);
  const cityData = useGameStore((s) => s.cityData);
  const activeTrackId = useGameStore((s) => s.activeTrackId);

  if (!modalTrackId || !cityData) return null;

  // Find the track
  const track = cityData.districts
    .flatMap((d) => d.tracks)
    .find((t) => t.id === modalTrackId);

  if (!track) return null;

  const isPlaying = activeTrackId === track.id;
  const isLocked = lockedTrackId === track.id;

  const handleClose = () => {
    closeModal();
  };

  const handleToggleLock = () => {
    lockTrack(track.id);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal card */}
      <div
        className="relative z-10 bg-gray-900/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden max-w-sm w-[90vw] max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Album art */}
        <div className="aspect-square w-full bg-gray-800">
          <img
            src={track.albumArt}
            alt={track.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Title + Artist */}
          <div>
            <h2 className="text-white text-xl font-bold leading-tight">
              {track.title}
            </h2>
            <p className="text-white/60 text-sm mt-0.5">{track.artist}</p>
          </div>

          {/* Shop name + flavor */}
          <div className="bg-white/5 rounded-lg px-3 py-2.5">
            <p className="text-festival-gold text-sm font-semibold">
              {track.shopName}
            </p>
            <p className="text-white/40 text-xs mt-0.5 italic">
              "{track.shopFlavor}"
            </p>
          </div>

          {/* Track metadata */}
          {track.metadata && (
            <div className="flex gap-3">
              {track.metadata.bpm && (
                <div className="bg-white/5 rounded-lg px-3 py-1.5 text-center flex-1">
                  <p className="text-white/30 text-[10px] uppercase tracking-wider">BPM</p>
                  <p className="text-white text-sm font-bold">{track.metadata.bpm}</p>
                </div>
              )}
              {track.metadata.key && (
                <div className="bg-white/5 rounded-lg px-3 py-1.5 text-center flex-1">
                  <p className="text-white/30 text-[10px] uppercase tracking-wider">Key</p>
                  <p className="text-white text-sm font-bold">{track.metadata.key}</p>
                </div>
              )}
              {track.metadata.label && (
                <div className="bg-white/5 rounded-lg px-3 py-1.5 text-center flex-1">
                  <p className="text-white/30 text-[10px] uppercase tracking-wider">Label</p>
                  <p className="text-white text-xs font-bold truncate">{track.metadata.label}</p>
                </div>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-2.5 pt-1">
            {/* Lock toggle */}
            <button
              onClick={handleToggleLock}
              className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all ${
                isLocked
                  ? 'bg-festival-gold text-black'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              {isLocked ? '🔒 Locked In' : '🔓 Lock Track'}
            </button>

            {/* YouTube link */}
            <a
              href={`https://www.youtube.com/watch?v=${track.embedId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-2.5 rounded-xl bg-red-600/20 text-red-400 hover:bg-red-600/30 font-semibold text-sm text-center transition-all"
            >
              ▶ YouTube
            </a>
          </div>

          {/* Now playing indicator */}
          {isPlaying && (
            <div className="flex items-center justify-center gap-2 text-festival-mint text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-festival-mint animate-pulse" />
              Now Playing
            </div>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 text-white/60 hover:text-white hover:bg-black/60 flex items-center justify-center text-lg transition-all"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
